import React, { useState } from 'react';
import {
    Box, Button, Collapse, MenuItem, Select, TextField, Typography,
    FormControlLabel, Switch, IconButton, Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs, { Dayjs } from 'dayjs';

import { OrganizationLookup } from './organization-lookup';
import { AppliedPill, FilterState } from './helpers';
import { ALE } from './tokens';
import {
    API_SOURCE_OPTIONS, METHOD_OPTIONS, STATUS_OPTIONS, TIME_PRESET_OPTIONS,
} from '../../builders/audit-log-explorer-builders';

const labelSx = {
    fontSize: '10.5px',
    fontWeight: 600,
    letterSpacing: '.4px',
    textTransform: 'uppercase' as const,
    color: ALE.tFaint,
};
const selectSx = {
    fontSize: '13px',
    minHeight: '36px',
    borderRadius: '7px',
    bgcolor: ALE.g0,
    '& .MuiSelect-select': { padding: '7px 30px 7px 11px' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: ALE.g300 },
};
const inputSx = {
    '& .MuiInputBase-root': { fontSize: '13px', borderRadius: '7px', minHeight: '36px' },
    '& .MuiInputBase-input': { padding: '7px 11px' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: ALE.g300 },
};

// Guard against invalid typed dates: an Invalid `Dayjs` throws "Invalid time
// value" on .toISOString(), which would crash the render. Returns null instead.
const toIso = (d: Dayjs | null): string | null => (d && d.isValid() ? d.toISOString() : null);

function Field({ label, children, minWidth = 150 }: { label: string; children: React.ReactNode; minWidth?: number }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth }}>
            <Typography sx={labelSx}>{label}</Typography>
            {children}
        </Box>
    );
}

function MfField({ label, hint, children, span = 1 }: { label: string; hint?: string; children: React.ReactNode; span?: number }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', gridColumn: `span ${span}` }}>
            <Typography sx={{ fontSize: '11.5px', fontWeight: 600, color: ALE.tSecondary }}>
                {label}{hint && <Box component="span" sx={{ ml: 0.75, fontWeight: 400, color: ALE.tFaint }}>{hint}</Box>}
            </Typography>
            {children}
        </Box>
    );
}

export interface AuditFilterBarProps {
    filters: FilterState;
    onPatch: (patch: Partial<FilterState>) => void;
    onSearch: () => void;
    onExport?: () => void;
    searching?: boolean;
    appliedPills: AppliedPill[];
    onRemovePill: (key: AppliedPill['key']) => void;
    onClearAll: () => void;
    onResolveOrgNames?: (map: Record<string, string>) => void;
}

export const AuditFilterBar: React.FC<AuditFilterBarProps> = ({
    filters, onPatch, onSearch, onExport, searching,
    appliedPills, onRemovePill, onClearAll, onResolveOrgNames,
}) => {
    const [showMore, setShowMore] = useState(false);
    const isCustom = filters.timePreset === 'custom';
    const onEnter = (e: React.KeyboardEvent) => { if (e.key === 'Enter') onSearch(); };

    return (
        <Box>
            {/* primary bar */}
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '10px', p: '14px 16px', flexWrap: 'wrap', borderBottom: `1px solid ${ALE.border}` }}>
                <OrganizationLookup
                    value={filters.subscriptionIds}
                    onChange={(subs) => onPatch({ subscriptionIds: subs })}
                    onResolveNames={onResolveOrgNames}
                />
                <Field label="Time range" minWidth={130}>
                    <Select size="small" sx={selectSx} value={filters.timePreset} onChange={(e) => onPatch({ timePreset: e.target.value as FilterState['timePreset'] })}>
                        {TIME_PRESET_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value} sx={{ fontSize: '13px' }}>{o.label}</MenuItem>)}
                    </Select>
                </Field>
                {isCustom && (
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Field label="Start" minWidth={190}>
                            <DateTimePicker
                                value={filters.startDate ? dayjs(filters.startDate) : null}
                                onChange={(d: Dayjs | null) => onPatch({ startDate: toIso(d) })}
                                maxDateTime={filters.endDate ? dayjs(filters.endDate) : undefined}
                                renderInput={(p) => <TextField {...p} size="small" sx={{ ...inputSx, width: 200 }} />}
                            />
                        </Field>
                        <Field label="End" minWidth={190}>
                            <DateTimePicker
                                value={filters.endDate ? dayjs(filters.endDate) : null}
                                onChange={(d: Dayjs | null) => onPatch({ endDate: toIso(d) })}
                                minDateTime={filters.startDate ? dayjs(filters.startDate) : undefined}
                                renderInput={(p) => <TextField {...p} size="small" sx={{ ...inputSx, width: 200 }} />}
                            />
                        </Field>
                    </LocalizationProvider>
                )}
                <Field label="Status" minWidth={120}>
                    <Select size="small" sx={selectSx} value={filters.statusClass} displayEmpty onChange={(e) => onPatch({ statusClass: e.target.value as FilterState['statusClass'] })}>
                        {STATUS_OPTIONS.map((o) => <MenuItem key={o.value || 'any'} value={o.value} sx={{ fontSize: '13px' }}>{o.label}</MenuItem>)}
                    </Select>
                </Field>
                <Field label="Method" minWidth={120}>
                    <Select size="small" sx={selectSx} value={filters.method} displayEmpty onChange={(e) => onPatch({ method: e.target.value as FilterState['method'] })}>
                        {METHOD_OPTIONS.map((o) => <MenuItem key={o.value || 'any'} value={o.value} sx={{ fontSize: '13px' }}>{o.label}</MenuItem>)}
                    </Select>
                </Field>
                <Field label="API source" minWidth={130}>
                    <Select size="small" sx={selectSx} value={filters.apiSource} displayEmpty onChange={(e) => onPatch({ apiSource: e.target.value })}>
                        {API_SOURCE_OPTIONS.map((o) => <MenuItem key={o.value || 'any'} value={o.value} sx={{ fontSize: '13px' }}>{o.label}</MenuItem>)}
                    </Select>
                </Field>

                <Button
                    onClick={() => setShowMore((s) => !s)}
                    endIcon={<ExpandMoreIcon sx={{ transform: showMore ? 'rotate(180deg)' : 'none', transition: 'transform .18s' }} />}
                    sx={{ textTransform: 'none', fontWeight: 600, fontSize: '13px', color: ALE.pMain, alignSelf: 'flex-end', height: 36 }}
                >
                    {showMore ? 'Fewer filters' : 'More filters'}
                </Button>

                <Box sx={{ flex: 1 }} />
                <Button
                    onClick={onSearch}
                    disabled={searching}
                    startIcon={<SearchIcon sx={{ fontSize: '16px !important' }} />}
                    sx={{
                        height: 36, px: 2, borderRadius: '7px', textTransform: 'none', fontWeight: 600, fontSize: '13px',
                        bgcolor: ALE.pMain, color: '#fff', '&:hover': { bgcolor: '#0f97cc' },
                    }}
                >
                    Search
                </Button>
                {onExport && (
                    <Tooltip title="Export results (CSV)">
                        <IconButton onClick={onExport} sx={{ width: 36, height: 36, borderRadius: '7px', border: `1px solid ${ALE.g300}`, color: ALE.tSecondary }}>
                            <FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>

            {/* more filters */}
            <Collapse in={showMore}>
                <Box sx={{
                    p: '18px 16px', borderBottom: `1px solid ${ALE.border}`, bgcolor: ALE.headerStrip,
                    display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2,1fr)', lg: 'repeat(3,1fr)' }, gap: '16px 20px',
                }}>
                    <MfField label="User ID / email">
                        <TextField size="small" sx={inputSx} placeholder="user@company.com or 24-char id…" value={filters.userIdOrEmail} onChange={(e) => onPatch({ userIdOrEmail: e.target.value })} onKeyDown={onEnter} />
                    </MfField>
                    <MfField label="Endpoint" hint="contains">
                        <TextField size="small" sx={inputSx} placeholder="/sosAlert, /getActiveAlert…" value={filters.endpoint} onChange={(e) => onPatch({ endpoint: e.target.value })} onKeyDown={onEnter} />
                    </MfField>
                    <MfField label="Response time (ms)">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <TextField size="small" type="number" sx={inputSx} placeholder="min" value={filters.responseTimeMin} onChange={(e) => onPatch({ responseTimeMin: e.target.value })} onKeyDown={onEnter} />
                            <Box component="span" sx={{ color: ALE.tFaint }}>—</Box>
                            <TextField size="small" type="number" sx={inputSx} placeholder="max" value={filters.responseTimeMax} onChange={(e) => onPatch({ responseTimeMax: e.target.value })} onKeyDown={onEnter} />
                        </Box>
                    </MfField>
                    <MfField label="Error message" hint="contains">
                        <TextField size="small" sx={inputSx} placeholder="e.g. timeout, ECONNREFUSED…" value={filters.errorMessageContains} onChange={(e) => onPatch({ errorMessageContains: e.target.value })} onKeyDown={onEnter} />
                    </MfField>
                    <MfField label="Has error">
                        <FormControlLabel
                            control={<Switch checked={filters.hasError} onChange={(e) => onPatch({ hasError: e.target.checked })} size="small" />}
                            label={<Typography sx={{ fontSize: '13px', color: ALE.tSecondary }}>Only requests with errors</Typography>}
                        />
                    </MfField>
                    <MfField label="Free-text search" hint="across request & response bodies" span={1}>
                        <TextField size="small" sx={inputSx} placeholder='e.g. "rate_limit_exceeded", an order id…' value={filters.freeText} onChange={(e) => onPatch({ freeText: e.target.value })} onKeyDown={onEnter} />
                    </MfField>
                </Box>
            </Collapse>

            {/* applied pills */}
            {appliedPills.length > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', p: '12px 16px', flexWrap: 'wrap', borderBottom: `1px solid ${ALE.border}` }}>
                    <Typography sx={{ ...labelSx, mr: '2px' }}>Filters</Typography>
                    {appliedPills.map((pill) => (
                        <Box key={pill.key} sx={{
                            display: 'inline-flex', alignItems: 'center', gap: '6px',
                            bgcolor: 'rgba(17,167,226,.08)', border: '1px solid rgba(17,167,226,.28)', color: ALE.pDark,
                            borderRadius: '16px', padding: '4px 6px 4px 11px', fontSize: '12px', fontWeight: 500,
                        }}>
                            {pill.label}
                            <Box onClick={() => onRemovePill(pill.key)} sx={{
                                display: 'flex', cursor: 'pointer', borderRadius: '50%', opacity: 0.7, '&:hover': { opacity: 1 },
                            }}>
                                <CloseIcon sx={{ fontSize: 13 }} />
                            </Box>
                        </Box>
                    ))}
                    <Typography onClick={onClearAll} sx={{ ml: '4px', color: ALE.tMuted, fontSize: '12.5px', fontWeight: 600, cursor: 'pointer', '&:hover': { color: ALE.errorT } }}>
                        Clear all
                    </Typography>
                </Box>
            )}
        </Box>
    );
};
